import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://nextdayrefund.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;

/**
 * Drop-in SEO head for every page.
 * All props optional — smart defaults fall through.
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  jsonLd = null,       // array or single schema object
  breadcrumbs = null,  // [{ name, url }]
}) {
  const fullTitle = title
    ? `${title} | NextDayRefund.com`
    : "NextDayRefund.com | Find Local Tax Pros — Next-Day Refund Advances";
  const metaDesc =
    description ||
    "Find a verified local tax professional who offers next-day refund advances. Search any US ZIP code. All 50 states. Free. 2027 tax season.";
  const canonicalUrl = canonical ? `${SITE}${canonical}` : null;

  // Build breadcrumb schema if provided
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length
      ? {
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: b.url.startsWith("http") ? b.url : `${SITE}${b.url}`,
          })),
        }
      : null;

  const schemas = [
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
  ];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl || SITE} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NextDayRefund.com" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {schemas.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@graph": schemas })}
        </script>
      )}
    </Helmet>
  );
}
